import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingCart, TrendingUp, BarChart3, Layers } from "lucide-react";
import { Annotation, AnnotationBanner } from "@/components/Annotation";
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fINR = (v) => { if (!v) return "0"; if (v >= 10000000) return `${(v/10000000).toFixed(2)} Cr`; if (v >= 100000) return `${(v/100000).toFixed(1)} L`; if (v >= 1000) return `${(v/1000).toFixed(0)}K`; return Math.round(v).toLocaleString(); };
const fQty = (v) => { if (!v) return "0"; if (v >= 100000) return `${(v/1000).toFixed(0)}K`; return v.toLocaleString(); };
const COLORS = ["#3b82f6","#10b981","#f59e0b","#8b5cf6","#f43f5e","#06b6d4","#ec4899","#84cc16","#0ea5e9","#a855f7","#f97316","#14b8a6","#6366f1","#e11d48","#22c55e"];

const Tip = ({ active, payload, label, isQty: tipQty }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
      <p className="text-slate-300 font-medium mb-1">{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color }} className="font-mono">{p.name}: {tipQty ? fQty(p.value) : `INR ${fINR(p.value)}`}</p>)}
    </div>
  );
};

export default function SalesAnalysis() {
  const [data2526, setData2526] = useState(null);
  const [data2425, setData2425] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loading2425, setLoading2425] = useState(false);
  const [error2425, setError2425] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState("ALL");
  const [selectedMonth, setSelectedMonth] = useState("ALL");
  const [trendChannel, setTrendChannel] = useState("ALL");
  const [trendCategories, setTrendCategories] = useState([]);
  const [mode, setMode] = useState("revenue"); // "revenue" or "quantity"
  const [fy, setFy] = useState("2526"); // "2526" or "2425"

  // Load FY25-26 on mount
  useEffect(() => {
    axios.get(`${API}/financial/sales-analysis`)
      .then((r) => { setData2526(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Load FY24-25 when user clicks the toggle
  const loadFY2425 = () => {
    setLoading2425(true);
    setError2425(null);
    axios.get(`${API}/financial/sales-analysis-2425`)
      .then((r) => {
        if (r.data && r.data.channel_sales) {
          setData2425(r.data);
        } else {
          setError2425("Invalid data received");
        }
        setLoading2425(false);
      })
      .catch((e) => {
        setError2425(`Failed to load: ${e.message}`);
        setLoading2425(false);
      });
  };

  const handleFySwitch = (year) => {
    setFy(year);
    setSelectedMonth("ALL");
    setTrendCategories([]);
    if (year === "2425" && !data2425 && !loading2425) {
      loadFY2425();
    }
  };

  const data = fy === "2526" ? data2526 : data2425;
  if (loading) return <div className="h-64 skeleton-shimmer rounded" />;

  // Show loading state for FY24-25
  if (fy === "2425" && loading2425) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">Sales Analysis</h1></div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-slate-800/60 border border-slate-700/50 rounded-md p-0.5">
              <button className="px-3 py-1.5 text-xs font-medium rounded bg-emerald-600 text-white shadow">FY 24-25</button>
              <button onClick={() => handleFySwitch("2526")} className="px-3 py-1.5 text-xs font-medium rounded text-slate-400 hover:text-slate-200">FY 25-26</button>
            </div>
          </div>
        </div>
        <div className="h-64 skeleton-shimmer rounded" />
      </div>
    );
  }

  if (!data || (!data.channel_sales?.length && !data.channel_qty?.length)) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">Sales Analysis</h1>
            <p className="text-sm text-slate-400 mt-1">Channel &amp; Category Performance</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-slate-800/60 border border-slate-700/50 rounded-md p-0.5" data-testid="fy-toggle">
              <button onClick={() => handleFySwitch("2425")} className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${fy === "2425" ? "bg-emerald-600 text-white shadow" : "text-slate-400 hover:text-slate-200"}`}>FY 24-25</button>
              <button onClick={() => handleFySwitch("2526")} className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${fy === "2526" ? "bg-emerald-600 text-white shadow" : "text-slate-400 hover:text-slate-200"}`}>FY 25-26</button>
            </div>
          </div>
        </div>
        <div className="glass-card rounded-md p-8 text-center">
          <p className="text-sm text-slate-400 mb-2">No data available for {fy === "2425" ? "FY 2024-25" : "FY 2025-26"}.</p>
          {error2425 && <p className="text-xs text-red-400 mb-3">{error2425}</p>}
          <button onClick={loadFY2425} className="text-xs text-blue-400 hover:text-blue-300 underline" data-testid="retry-btn">
            Retry Loading
          </button>
        </div>
      </div>
    );
  }

  const isQty = mode === "quantity";
  const channels = isQty ? (data.channel_qty || []) : (data.channel_sales || []);
  const categories = isQty ? (data.category_qty || []) : (data.category_sales || []);
  const channelCats = isQty ? (data.channel_categories_qty || {}) : (data.channel_categories || {});
  const summary = data.summary || {};
  const qtySummary = data.qty_summary || {};
  const monthLabels = data.month_labels || [];
  const monthKeys = data.months || [];

  // Channel monthly trend data
  const channelTrend = monthKeys.map((mk, i) => {
    const entry = { month: monthLabels[i] };
    channels.forEach((ch) => { entry[ch.name] = ch.monthly?.[mk] || 0; });
    return entry;
  });

  // Channel pie data
  const channelPie = channels.filter(c => c.total > 0).map(c => ({ name: c.name, value: c.total }));
  const totalRev = channels.reduce((s, c) => s + c.total, 0);

  // Format helper based on mode
  const fVal = isQty ? fQty : fINR;
  const valPrefix = isQty ? "" : "INR ";
  const valLabel = isQty ? "Units" : "Revenue";

  // Category bar data (sorted)
  const catBar = categories.map(c => ({ name: c.name, value: c.total }));

  // Per-channel category breakdown with optional month filter
  const activeCats = selectedChannel === "ALL" ? categories : (channelCats[selectedChannel] || []);
  const activeCatsChart = activeCats.map(c => {
    const value = selectedMonth === "ALL" ? c.total : (c.monthly?.[selectedMonth] || 0);
    return { name: c.name, value };
  }).sort((a, b) => b.value - a.value);

  // Category monthly trend with channel & category filters
  const trendSourceCats = trendChannel === "ALL" ? categories : (channelCats[trendChannel] || []);
  const allCatNames = trendSourceCats.map(c => c.name);
  // Initialize trendCategories with top 5 on first load
  const activeTrendCats = trendCategories.length > 0
    ? trendSourceCats.filter(c => trendCategories.includes(c.name))
    : trendSourceCats.slice(0, 5);
  const catTrend = monthKeys.map((mk, i) => {
    const entry = { month: monthLabels[i] };
    activeTrendCats.forEach((c) => { entry[c.name] = c.monthly?.[mk] || 0; });
    return entry;
  });

  return (
    <div className="space-y-5" data-testid="sales-analysis-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">Sales Analysis</h1>
          <p className="text-sm text-slate-400 mt-1">{data.period} &middot; Channel &amp; Category Performance</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-slate-800/60 border border-slate-700/50 rounded-md p-0.5" data-testid="fy-toggle">
            <button onClick={() => handleFySwitch("2425")}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${fy === "2425" ? "bg-emerald-600 text-white shadow" : "text-slate-400 hover:text-slate-200"}`}
              data-testid="fy-2425">FY 24-25</button>
            <button onClick={() => handleFySwitch("2526")}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${fy === "2526" ? "bg-emerald-600 text-white shadow" : "text-slate-400 hover:text-slate-200"}`}
              data-testid="fy-2526">FY 25-26</button>
          </div>
          <div className="flex items-center bg-slate-800/60 border border-slate-700/50 rounded-md p-0.5" data-testid="mode-toggle">
            <button
              onClick={() => setMode("revenue")}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${!isQty ? "bg-blue-600 text-white shadow" : "text-slate-400 hover:text-slate-200"}`}
              data-testid="mode-revenue"
            >Revenue</button>
            <button
              onClick={() => setMode("quantity")}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${isQty ? "bg-blue-600 text-white shadow" : "text-slate-400 hover:text-slate-200"}`}
              data-testid="mode-quantity"
            >Quantity</button>
          </div>
          <Badge variant="outline" className="text-emerald-400 border-emerald-800 bg-emerald-950/30 text-xs font-mono">
            Total: {valPrefix}{fVal(totalRev)}
          </Badge>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: `Total ${valLabel}`, value: `${valPrefix}${fVal(totalRev)}`, color: "text-emerald-400" },
          { label: "Active Channels", value: channels.length, color: "text-blue-400" },
          { label: "Top Channel", value: isQty ? qtySummary.top_channel : summary.top_channel, color: "text-amber-400" },
          { label: "Top Category", value: isQty ? qtySummary.top_category : summary.top_category, color: "text-violet-400" },
        ].map((k) => (
          <div key={k.label} className="glass-card rounded-md p-3">
            <p className="text-[9px] text-slate-500 uppercase tracking-wider">{k.label}</p>
            <p className={`font-mono text-base mt-0.5 ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Channel Revenue + Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <Card className="lg:col-span-7 border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="channel-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-400" /> Monthly Revenue by Channel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={channelTrend}>
                <defs>
                  {channels.slice(0, 5).map((ch, i) => (
                    <linearGradient key={ch.name} id={`sg${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS[i]} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={COLORS[i]} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={fVal} />
                <Tooltip content={<Tip isQty={isQty} />} />
                {channels.slice(0, 5).map((ch, i) => (
                  <Area key={ch.name} type="monotone" dataKey={ch.name} name={ch.name}
                    stroke={COLORS[i]} fill={`url(#sg${i})`} strokeWidth={1.5} stackId="1" />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-5 border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="channel-pie-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <Layers className="w-4 h-4 text-violet-400" /> Channel Mix
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={channelPie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={75} stroke="#0f172a" strokeWidth={2}>
                  {channelPie.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0];
                  return (
                    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
                      <p className="text-slate-200 font-semibold">{d.name}</p>
                      <p className="font-mono text-emerald-400">{valPrefix}{fVal(d.value)}</p>
                      <p className="text-slate-400">{totalRev > 0 ? ((d.value / totalRev) * 100).toFixed(1) : 0}% share</p>
                    </div>
                  );
                }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {channels.map((c, i) => (
                <div key={c.name} className="flex items-center gap-1.5 text-[10px]">
                  <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: COLORS[i] }} />
                  <span className="text-slate-400 truncate">{c.name}</span>
                  <span className="font-mono text-slate-500 ml-auto">{totalRev > 0 ? ((c.total / totalRev) * 100).toFixed(0) : 0}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <AnnotationBanner title="Channel Performance — FY25-26">
        <p>Amazon leads with INR {fINR(channels.find(c => c.name === 'AMAZON')?.total)} ({totalRev > 0 ? ((channels.find(c => c.name === 'AMAZON')?.total / totalRev) * 100).toFixed(0) : 0}% share). Website/App contributes INR {fINR(channels.find(c => c.name === 'WEBSITE')?.total)} — the highest-margin channel with zero marketplace commission and no returns. New channels Blinkit (INR {fINR(channels.find(c => c.name === 'BLINKIT')?.total)}) and Nykaa are gaining traction, demonstrating successful channel diversification. Offline stores (INR {fINR(channels.find(c => c.name === 'STORE')?.total)}) represent the early phase of physical retail expansion launched Feb 2025.</p>
      </AnnotationBanner>

      {/* Category Analysis */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="category-bar-chart">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-400" /> Category Revenue
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-28 h-8 text-xs bg-slate-900 border-slate-700 text-slate-200" data-testid="month-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="ALL">All Months</SelectItem>
                  {monthKeys.map((mk, i) => (
                    <SelectItem key={mk} value={mk}>{monthLabels[i]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                <SelectTrigger className="w-36 h-8 text-xs bg-slate-900 border-slate-700 text-slate-200" data-testid="channel-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="ALL">All Channels</SelectItem>
                  {Object.keys(channelCats).map((ch) => (
                    <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={380}>
            <BarChart data={activeCatsChart} layout="vertical" margin={{ left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={fVal} />
              <YAxis dataKey="name" type="category" width={175} tick={{ fill: '#94a3b8', fontSize: 9 }} tickLine={false} axisLine={false} />
              <Tooltip content={<Tip isQty={isQty} />} />
              <Bar dataKey="value" name={valLabel} radius={[0, 4, 4, 0]}>
                {activeCatsChart.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Category Monthly Trend */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="category-trend-chart">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-400" /> Category Monthly Trend
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={trendChannel} onValueChange={(v) => { setTrendChannel(v); setTrendCategories([]); }}>
                <SelectTrigger className="w-36 h-8 text-xs bg-slate-900 border-slate-700 text-slate-200" data-testid="trend-channel-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="ALL">All Channels</SelectItem>
                  {Object.keys(channelCats).map((ch) => (
                    <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Category multi-select chips */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {allCatNames.map((name, i) => {
              const isSelected = trendCategories.length === 0
                ? trendSourceCats.slice(0, 5).some(c => c.name === name)
                : trendCategories.includes(name);
              return (
                <button
                  key={name}
                  onClick={() => {
                    if (trendCategories.length === 0) {
                      // First click: initialize from default top 5, then toggle
                      const defaults = trendSourceCats.slice(0, 5).map(c => c.name);
                      if (defaults.includes(name)) {
                        setTrendCategories(defaults.filter(n => n !== name));
                      } else {
                        setTrendCategories([...defaults, name]);
                      }
                    } else {
                      setTrendCategories(prev =>
                        prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
                      );
                    }
                  }}
                  className={`px-2 py-0.5 rounded text-[10px] transition-all ${
                    isSelected
                      ? "bg-blue-600/20 border border-blue-500/40 text-blue-300"
                      : "bg-slate-800/40 border border-slate-700/40 text-slate-500 hover:text-slate-300"
                  }`}
                  data-testid={`cat-chip-${name}`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={catTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={fVal} />
              <Tooltip content={<Tip isQty={isQty} />} />
              {activeTrendCats.map((c, i) => (
                <Line key={c.name} type="monotone" dataKey={c.name} name={c.name}
                  stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 2.5, fill: COLORS[i % COLORS.length] }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Channel-wise Revenue Table */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="channel-table">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200">Channel Revenue (Monthly)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left py-1.5 px-2 text-[10px] text-slate-500 uppercase w-28">Channel</th>
                {monthLabels.map((m) => <th key={m} className="text-right py-1.5 px-2 text-[10px] text-slate-500 uppercase">{m}</th>)}
                <th className="text-right py-1.5 px-2 text-[10px] text-emerald-600 uppercase font-bold">Total</th>
                <th className="text-right py-1.5 px-2 text-[10px] text-slate-500 uppercase">Share</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((ch, i) => (
                <tr key={ch.name} className="border-b border-slate-800/20 hover:bg-slate-800/20">
                  <td className="py-1.5 px-2 text-xs text-slate-200 flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: COLORS[i] }} />
                    {ch.name}
                  </td>
                  {monthKeys.map((mk) => (
                    <td key={mk} className="py-1.5 px-2 text-right font-mono text-[10px] text-slate-400">
                      {fVal(ch.monthly?.[mk])}
                    </td>
                  ))}
                  <td className="py-1.5 px-2 text-right font-mono text-[10px] text-emerald-400 font-semibold">{fVal(ch.total)}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-[10px] text-blue-400">{totalRev > 0 ? ((ch.total / totalRev) * 100).toFixed(1) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
